import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAiWorkspaceViewport } from "../hooks/useAiWorkspaceViewport";
import { Sparkles } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import HealthBadge from "./HealthBadge";
import MobileTabBar from "./MobileTabBar";
import AvatarMenu from "./AvatarMenu";
import AlertBell from "./alerts/AlertBell";

function NavItem({ to, children, testId, end = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `rounded-md px-3 py-1.5 text-sm transition-colors ${
          isActive
            ? "bg-zinc-800 text-zinc-50"
            : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, isAdmin } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  // V4.10 UX — /ai is a WORKSPACE page: the page itself never scrolls
  // (header + composer stay fixed, only the thread/canvas columns
  // scroll). Container height follows the visualViewport so on mobile
  // web (iOS Safari) the composer sits directly above the keyboard.
  const isAiWorkspace = location.pathname === "/ai";
  // Master-Sprint P0 — /heatmap is map-centred on mobile: compact
  // paddings so the map is immediately visible without scrolling.
  const isMapWorkspace = location.pathname === "/heatmap";
  // Master-Hotfix P0.2 — ONE central viewport/keyboard hook. Layout
  // contract lives in useAiWorkspaceViewport.js (rAF-mirrored
  // visualViewport while keyboard open, 100dvh otherwise, document
  // scroll-locked, bottom nav hidden on keyboard).
  useAiWorkspaceViewport(isAiWorkspace);

  // The public marketing landings (`/` and `/landing-v2`) render their
  // own dedicated nav + footer (premium sales-oriented chrome — different
  // palette and structure than the app shell). Skip the global Layout
  // chrome there so the landings have full control of presentation.
  // Phase 1.5 — "/" is the marketplace home. Anonymous visitors get the
  // page's own public chrome; logged-in users get the full app shell so
  // anonymous → free → pro feels like ONE product.
  if (
    (location.pathname === "/" && !user)
    || location.pathname === "/landing-v2"
    || location.pathname === "/welcome"
  ) {
    return <>{children}</>;
  }

  return (
    <div
      className={
        isAiWorkspace
          ? "fixed inset-x-0 flex w-full flex-col overflow-hidden bg-zinc-950 text-zinc-100"
          : "min-h-dvh bg-zinc-950 text-zinc-100"
      }
      style={
        isAiWorkspace
          ? {
              // Layout contract (Master-Hotfix P0.2): the workspace IS
              // the visual viewport. `top` follows the iOS focus pan
              // (NOT transform — transform created a containing block
              // that broke the fixed bottom nav).
              top: "var(--ai-vvoff, 0px)",
              height: "var(--ai-vvh, 100dvh)",
            }
          : undefined
      }
      data-testid="app-layout"
    >
      <header className="sticky top-0 z-30 shrink-0 border-b border-zinc-800/80 bg-zinc-950/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-6 py-3">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-2 font-mono text-sm tracking-tight transition-opacity hover:opacity-90"
          >
            <span className="inline-block h-2 w-2 -translate-y-0.5 bg-emerald-400" />
            <span className="font-semibold">DealAlert</span>
          </Link>

          {/* V4.7 P1 — DealAlert AI is the hero nav item (premium pill,
              gradient hairline border); the four daily activities stay
              as quiet text items. Everything account-ish (profile,
              settings, language, referral, tour, admin, logout) lives
              behind the avatar. */}
          <nav className="hidden items-center gap-1.5 md:flex" data-testid="primary-nav">
            {user && (
              <>
                <NavLink
                  to="/ai"
                  data-testid="nav-ai"
                  className={({ isActive }) =>
                    `ai-nav-pill relative mr-1.5 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm ${
                      isActive
                        ? "ai-nav-pill-active text-amber-200"
                        : "text-zinc-100 hover:text-amber-200"
                    }`
                  }
                >
                  <Sparkles size={13} strokeWidth={2} className="text-amber-400" aria-hidden />
                  <span className="font-medium tracking-tight">DealAlert AI</span>
                </NavLink>
                <NavItem to="/" end testId="nav-discover">{t("nav.discover")}</NavItem>
                <NavItem to="/heatmap" testId="nav-heatmap">{t("nav.heatmap")}</NavItem>
                <NavItem to="/alerts" testId="nav-alerts">{t("nav.alerts")}</NavItem>
                <NavItem to="/favorites" testId="nav-favorites">{t("nav.favorites")}</NavItem>
              </>
            )}
          </nav>

          <div className="flex items-center gap-2.5">
            {user ? (
              <>
                <AlertBell />
                <AvatarMenu />
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="rounded-md px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900"
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register"
                  className="rounded-md bg-emerald-500 px-3 py-1.5 text-sm font-medium text-zinc-950 hover:bg-emerald-400"
                >
                  {t("nav.register")}
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Bottom padding on mobile clears the fixed <MobileTabBar /> (64px
          + safe-area). Desktop reverts to the original py-10. In the AI
          workspace the main IS the viewport (flex, no page scroll).
          Master-Sprint P0 2026-02 — the AI workspace gets `max-w-none`
          so its inner `xl:max-w-7xl xl:grid-cols-[minmax(0,1fr)_400px]`
          two-column layout can actually breathe on wide desktops. All
          other pages keep the existing `max-w-6xl` — no global widening
          regression on Dashboard / Listings / Profil / etc. */}
      <main
        className={
          isAiWorkspace
            ? "ai-main mx-auto flex w-full max-w-none min-h-0 flex-1 flex-col px-4 pt-3 pb-[calc(64px+env(safe-area-inset-bottom,0)+0.25rem)] sm:px-6 md:pb-3"
            : isMapWorkspace
              ? "mx-auto max-w-6xl px-3 pb-[calc(64px+env(safe-area-inset-bottom,0)+0.5rem)] pt-3 md:px-6 md:pt-10 md:pb-10"
              : "mx-auto max-w-6xl px-6 pb-[calc(64px+env(safe-area-inset-bottom,0)+1rem)] pt-10 md:pb-10"
        }
      >
        {children}
      </main>

      {!isAiWorkspace && (
      <footer className="mt-16 border-t border-zinc-900/80 pb-[calc(64px+env(safe-area-inset-bottom,0)+1rem)] md:pb-0">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-3 px-6 py-6 text-xs text-zinc-500 sm:flex-row sm:items-center">
          <span>© 2026 DealAlert · canonical build</span>
          <nav className="flex items-center gap-4">
            <Link
              to="/support"
              className="text-zinc-500 hover:text-zinc-300 underline-offset-4 hover:underline"
              data-testid="footer-support-link"
            >
              Support
            </Link>
            <Link
              to="/impressum"
              className="text-zinc-500 hover:text-zinc-300 underline-offset-4 hover:underline"
              data-testid="footer-impressum-link"
            >
              Impressum
            </Link>
            <Link
              to="/datenschutz"
              className="text-zinc-500 hover:text-zinc-300 underline-offset-4 hover:underline"
              data-testid="footer-datenschutz-link"
            >
              Datenschutz
            </Link>
            <Link
              to="/agb"
              className="text-zinc-500 hover:text-zinc-300 underline-offset-4 hover:underline"
              data-testid="footer-agb-link"
            >
              AGB
            </Link>
            {/* Quick-Win 2026-06-18 — Health-Badges (api/db ok) sind ein
                ops/diagnostic-Signal und für Endkunden bedeutungslos. Wir
                rendern sie nur noch für Admins und entfernen sie aus dem
                DOM für reguläre User (kein `display:none` — wirklich raus,
                damit kein Layout-Whitespace bleibt). */}
            {isAdmin && <HealthBadge />}
          </nav>
        </div>
      </footer>
      )}

      {/* Mobile-only bottom tab bar — authed users only. */}
      <MobileTabBar />
    </div>
  );
}
